import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, Maximize2, X } from 'lucide-react';
import { useChat } from '../../hooks/useChat';
import { useVegaChart } from '../../hooks/useVegaChart';
import ChartAdjustmentPanel from '../ChartAdjustmentPanel';
import AdjustmentFeedback from '../AdjustmentFeedback';
import ChartErrorBoundary from '../ChartErrorBoundary';
import PinButton from '../PinButton';
import { theme, getGradient, getShadow } from '../../styles/theme';

// Helper function to decode Unicode escape sequences
const decodeUnicodeText = (text) => {
  if (typeof text !== 'string') return text;

  try {
    // Replace Unicode escape sequences with actual characters
    return text.replace(/\\u[\dA-F]{4}/gi, (match) => {
      return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
    });
  } catch (error) {
    return text;
  }
};

// Language detection helper
const detectLanguage = (text) => {
  if (!text) return 'english';

  // First decode any Unicode sequences
  const decodedText = decodeUnicodeText(text);

  // Check for Arabic characters
  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;

  if (arabicRegex.test(decodedText)) {
    return 'arabic';
  }

  return 'english';
};

const DataSection = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  border-radius: ${theme.borderRadius.large};
  
  ${props => props.type === 'raw_data' && `
    background: ${theme.colors.backgroundMuted};
    border: 1px solid ${theme.colors.borderLight};
  `}
  
  ${props => props.type === 'formatted_result' && `
    background: linear-gradient(135deg, #fef5e7 0%, #fed7aa 100%);
    border: 1px solid #fdba74;
  `}
  
  ${props => props.type === 'reasoning' && `
    background: linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%);
    border: 1px solid #29b6f6;
  `}
  
  ${props => props.type === 'suggestion' && `
    background: linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%);
    border: 1px solid #c084fc;
  `}
  
  ${props => props.type === 'raw_value' && `
    background: linear-gradient(135deg, #f0f9ff 0%, #dbeafe 100%);
    border: 1px solid #60a5fa;
  `}

  .label {
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textSecondary};
    margin-bottom: 8px;
    direction: rtl;
    text-align: right;
  }

  .content {
    color: ${theme.colors.textPrimary};
    line-height: ${theme.typography.lineHeight.relaxed};
    white-space: pre-wrap;
    word-break: break-word;
    direction: auto;
    text-align: start;
    unicode-bidi: plaintext;
  }
  
  .content.arabic {
    direction: rtl;
    text-align: right;
  }
  
  .content.english {
    direction: ltr;
    text-align: left;
  }
`;

const VegaContainer = styled.div`
  background: #f8fafc;
  border: 2px dashed #4299e1;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  margin-top: 8px;
  width: 100%;
  position: relative;
  overflow-x: auto;

  .chart-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 300px;

    > div {
      margin: 0 auto;
      width: 100%;
      max-width: 100%;

      .vega-embed {
        margin: 0 auto !important;
        text-align: center;
        width: 100%;

        details {
          display: none;  /* Hide Vega actions menu */
        }
      }

      canvas, svg {
        display: block !important;
        margin: 0 auto !important;
        max-width: 100% !important;
        height: auto !important;
      }
    }
  }
`;

const ChartActions = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  direction: rtl;
`;

const AdjustButton = styled(motion.button)`
  background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(26, 139, 139, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const NoVisualization = styled.div`
  padding: 20px;
  background: linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%);
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.large};
  text-align: center;
  color: ${theme.colors.textMuted};
  font-weight: ${theme.typography.fontWeight.medium};
  margin: 16px 0;
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${theme.colors.textPrimary};
  margin-bottom: 16px;
  direction: auto;
  text-align: start;
  unicode-bidi: plaintext;
`;

const MultiChartContainer = styled.div`
  display: grid;
  grid-template-columns: ${props =>
    props.chartCount === 1 ? '1fr' :
    props.chartCount === 2 ? 'repeat(2, 1fr)' :
    'repeat(auto-fit, minmax(450px, 1fr))'
  };
  gap: 24px;
  margin-top: 16px;
  width: 100%;

  /* Stack on smaller screens */
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.large};
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  width: 100%;
  min-width: 0;  /* Allow shrinking below content size */
  overflow: hidden;  /* Prevent content overflow */
`;

// Modal styles
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 16px;
  width: 95vw;
  max-width: 1400px;
  height: 90vh;
  max-height: 900px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${theme.colors.textPrimary};
  margin: 0;
  direction: auto;
  unicode-bidi: plaintext;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: ${theme.colors.textSecondary};

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: ${theme.colors.textPrimary};
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  padding: 32px;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;

  .chart-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    > div {
      width: 100% !important;
      height: 100% !important;
    }
  }
`;

const ExpandButton = styled(motion.button)`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// Single chart renderer component
const SingleChartRenderer = ({
  chartItem,
  chartIndex,
  onAdjust,
  onExpand,
  canAdjust,
  isProcessing,
  // Pin functionality props
  sqlQuery,
  originalQuestion,
  columnNames,
  cachedResults
}) => {
  const vegaRef = useRef(null);

  // Use the chart spec as-is - backend now handles responsive sizing
  const vegaSpec = React.useMemo(() => {
    if (!chartItem?.chart_schema) {
      return null;
    }

    return chartItem.chart_schema;
  }, [chartItem?.chart_schema]);

  // Use the Vega chart hook for proper lifecycle management
  useVegaChart(vegaRef, vegaSpec);

  return (
    <ChartErrorBoundary>
      <VegaContainer>
        <div className="chart-container">
          <div ref={vegaRef} />
        </div>
        <ChartActions>
          <PinButton
            chartSpec={chartItem.chart_schema}
            sqlQuery={sqlQuery}
            originalQuestion={originalQuestion || chartItem.reasoning}
            title={chartItem.title || originalQuestion?.substring(0, 100)}
            columnNames={columnNames}
            cachedResults={cachedResults}
          />
          <ExpandButton
            onClick={() => onExpand(chartIndex)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Maximize2 />
            عرض بملء الشاشة
          </ExpandButton>
          {canAdjust && (
            <AdjustButton
              onClick={() => onAdjust(chartIndex)}
              disabled={isProcessing}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Settings />
              تعديل الرسم البياني {chartItem.unit && chartItem.unit !== 'default' ? `(${chartItem.unit})` : ''}
            </AdjustButton>
          )}
        </ChartActions>
      </VegaContainer>
    </ChartErrorBoundary>
  );
};

const DataVisualization = ({ message, userQuestion }) => {
  const { actions, isProcessing } = useChat();
  const [showAdjustPanel, setShowAdjustPanel] = useState(false);
  const [lastAdjustmentError, setLastAdjustmentError] = useState(null);
  const [selectedChartIndex, setSelectedChartIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalChartIndex, setModalChartIndex] = useState(0);
  const modalVegaRef = useRef(null);

  // Get charts array (always an array in new format)
  const charts = message.visualization?.charts || [];
  const isMultiChart = message.visualization?.is_multi_chart || false;
  const hasCharts = charts.length > 0;

  // COMPREHENSIVE DEBUG LOGGING
  console.log('🔍 FULL MESSAGE STRUCTURE:', {
    hasVisualization: !!message.visualization,
    visualizationKeys: message.visualization ? Object.keys(message.visualization) : [],
    chartsArray: message.visualization?.charts,
    chartsArrayLength: message.visualization?.charts?.length,
    hasVegaLiteSpec: !!message.visualization?.vega_lite_spec,
    vegaLiteSpecType: typeof message.visualization?.vega_lite_spec,
    isMultiChart: message.visualization?.is_multi_chart,
    reasoning: message.visualization?.reasoning?.substring(0, 100),
    chartType: message.visualization?.chart_type,
    messageId: message.id
  });

  // For backward compatibility: if old format (vega_lite_spec), convert to array
  const legacySpec = message.visualization?.vega_lite_spec;
  const chartsList = hasCharts ? charts : (legacySpec ? [{
    chart_schema: legacySpec,
    reasoning: message.visualization?.reasoning || '',
    chart_type: message.visualization?.chart_type || 'bar',
    unit: 'default',
    title: '',
    chart_index: 0
  }] : []);

  console.log('📊 FINAL CHARTS LIST:', {
    chartsListLength: chartsList.length,
    source: hasCharts ? 'NEW_FORMAT (charts array)' : (legacySpec ? 'LEGACY_FORMAT (vega_lite_spec)' : 'NONE'),
    chartsList: chartsList,
    firstChartHasData: chartsList[0]?.chart_schema?.data?.values ? chartsList[0].chart_schema.data.values.length : 0
  });

  const hasWorkingChart = chartsList.length > 0;

  const handleAdjustChart = React.useCallback((adjustmentOptions) => {
    if (!message.column_names || !message.results) {
      console.error('Cannot adjust chart: missing data');
      setLastAdjustmentError('Cannot adjust chart: missing data');
      return;
    }

    // Prevent adjustment if already processing
    if (isProcessing) {
      console.log('Chart adjustment already in progress, ignoring request');
      return;
    }

    // Clear any previous error when starting new adjustment
    setLastAdjustmentError(null);

    // For multi-chart scenarios, extract the data for the specific chart being adjusted
    const chartIndex = adjustmentOptions.chart_index || 0;
    const selectedChart = chartsList[chartIndex];

    // Create a modified message with chart-specific data
    const messageForAdjustment = { ...message };

    // Debug logging
    console.log('🔧 Chart adjustment debug:');
    console.log('  - isMultiChart:', isMultiChart);
    console.log('  - chartIndex:', chartIndex);
    console.log('  - chartsList length:', chartsList.length);
    console.log('  - selectedChart exists:', !!selectedChart);
    console.log('  - selectedChart.unit:', selectedChart?.unit);
    console.log('  - chart_schema exists:', !!selectedChart?.chart_schema);
    console.log('  - chart_schema.data exists:', !!selectedChart?.chart_schema?.data);
    console.log('  - chart_schema.data.values exists:', !!selectedChart?.chart_schema?.data?.values);
    console.log('  - chart_schema.data.values length:', selectedChart?.chart_schema?.data?.values?.length);

    // If this is a multi-chart scenario and the chart has its own data
    if (isMultiChart && selectedChart?.chart_schema?.data?.values) {
      const chartData = selectedChart.chart_schema.data.values;

      console.log('  - Chart data (first row):', chartData[0]);

      // Extract column names from the chart data
      if (chartData.length > 0) {
        const columnNames = Object.keys(chartData[0]);
        messageForAdjustment.column_names = columnNames;

        // Convert from List[Dict] to List[List] format (required by backend)
        // Backend expects: [[val1, val2, val3], [val1, val2, val3], ...]
        // Chart data is: [{col1: val1, col2: val2}, {col1: val1, col2: val2}, ...]
        messageForAdjustment.results = chartData.map(row =>
          columnNames.map(col => row[col])
        );

        console.log(`✅ Adjusting chart ${chartIndex} with ${messageForAdjustment.results.length} rows (filtered from ${message.results.length} total rows)`);
        console.log(`   Chart-specific columns: ${columnNames.join(', ')}`);
        console.log(`   Sample row:`, messageForAdjustment.results[0]);
      }
    } else {
      console.log('⚠️  Using full message data (chart-specific extraction failed or not multi-chart)');
    }

    // Start the adjustment (errors will be handled by useChat and shown as messages)
    actions.adjustChart(messageForAdjustment, adjustmentOptions);

    // Don't auto-close - let the user see the result and close manually
    // This prevents the "disappearing panel" issue
  }, [message, actions, isProcessing, chartsList, isMultiChart]);

  const handleExpandChart = React.useCallback((chartIndex) => {
    setModalChartIndex(chartIndex);
    setShowModal(true);
  }, []);

  const handleCloseModal = React.useCallback(() => {
    setShowModal(false);
  }, []);

  const hasDataButNoChart = message.visualization?.raw_data && !hasWorkingChart;

  // Check if chart can be adjusted (allow adjustment even if current chart failed)
  const hasValidData = message.column_names &&
                       message.results &&
                       message.results.length > 0;

  const canAdjustChart = hasValidData && !isProcessing;

  const availableColumns = message.column_names || [];

  // Extract current chart configuration from selected chart
  const selectedChart = chartsList[selectedChartIndex] || chartsList[0] || {};
  const currentChart = {
    chart_type: selectedChart.chart_type || 'bar',
    x_axis: selectedChart.chart_schema?.encoding?.x?.field || null,
    y_axis: selectedChart.chart_schema?.encoding?.y?.field || null,
    color: selectedChart.chart_schema?.encoding?.color?.field || null,
    theta: selectedChart.chart_schema?.encoding?.theta?.field || null,
    chart_index: selectedChart.chart_index || 0
  };

  // Extract chart-specific data for multi-chart scenarios
  // This ensures compatibility checking uses the correct data subset
  // NOTE: Use useMemo to recalculate when selectedChartIndex changes
  const hasMultipleCharts = chartsList.length > 1;

  const { chartSpecificData, chartSpecificColumns } = React.useMemo(() => {
    let data = null;
    let columns = null;

    console.log('🔍 Chart data extraction debug:', {
      isMultiChart,
      hasMultipleCharts,
      chartsCount: chartsList.length,
      selectedChartIndex,
      hasSelectedChart: !!selectedChart,
      hasChartSchema: !!selectedChart?.chart_schema,
      hasChartData: !!selectedChart?.chart_schema?.data,
      hasChartValues: !!selectedChart?.chart_schema?.data?.values,
      selectedChartUnit: selectedChart?.unit,
      chartSchemaKeys: selectedChart?.chart_schema ? Object.keys(selectedChart.chart_schema) : null,
      dataKeys: selectedChart?.chart_schema?.data ? Object.keys(selectedChart.chart_schema.data) : null
    });

    if (hasMultipleCharts && selectedChart?.chart_schema?.data?.values) {
      const chartData = selectedChart.chart_schema.data.values;

      if (chartData.length > 0) {
        columns = Object.keys(chartData[0]);
        // Convert from List[Dict] to List[List] format
        data = chartData.map(row =>
          columns.map(col => row[col])
        );

        console.log('✅ Chart-specific data extracted for compatibility check:', {
          chartIndex: selectedChartIndex,
          unit: selectedChart.unit,
          rows: data.length,
          columns: columns
        });
      }
    } else {
      console.log('⚠️ Chart-specific data extraction skipped:', {
        reason: !hasMultipleCharts ? `Not multi-chart (${chartsList.length} charts)` : 'No chart data values'
      });
    }

    return { chartSpecificData: data, chartSpecificColumns: columns };
  }, [selectedChartIndex, hasMultipleCharts, selectedChart, isMultiChart, chartsList.length]);

  // Handler for opening adjustment panel
  const handleOpenAdjust = React.useCallback((chartIndex = 0) => {
    setSelectedChartIndex(chartIndex);
    setShowAdjustPanel(true);
  }, []);

  // Early returns AFTER all hooks
  if (!message.visualization) {
    return null;
  }

  // Handle non-visualizable data - clean, minimal display
  if (message.visualization.visualization_recommended === false) {
    // Get the display value (prefer formatted_result, fallback to raw_value)
    const displayValue = message.visualization.formatted_result ||
                         message.visualization.raw_value;

    return (
      <VegaContainer style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '24px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '32px',
          marginBottom: '12px',
          filter: 'grayscale(0.3)'
        }}>
          {message.visualization.result_type === 'single_aggregated_scalar' ||
           message.visualization.result_type === 'single_scalar' ? '🔢' :
           message.visualization.result_type?.includes('string') ||
           message.visualization.result_type === 'tabular_strings' ? '📋' : '📊'}
        </div>

        {/* Show the value prominently */}
        {displayValue && (
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#1e293b',
            marginBottom: '8px',
            direction: 'auto',
            unicodeBidi: 'plaintext'
          }}>
            {String(displayValue)}
          </div>
        )}

        <div style={{
          fontSize: '14px',
          color: '#64748b',
          lineHeight: '1.5'
        }}>
          {message.visualization.result_type === 'single_aggregated_scalar' ||
           message.visualization.result_type === 'single_scalar'
            ? 'هذه الإجابة قيمة واحدة - لا تحتاج إلى رسم بياني'
            : 'لا يمكن تمثيل هذه البيانات في رسم بياني'}
        </div>
      </VegaContainer>
    );
  }

  return (
    <>
      {hasDataButNoChart ? (
        <VegaContainer style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '32px',
            marginBottom: '12px',
            filter: 'grayscale(0.3)'
          }}>
            📊
          </div>
          <div style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#475569',
            marginBottom: '8px'
          }}>
            نتيجة رقمية
          </div>
          <div style={{
            fontSize: '14px',
            color: '#64748b',
            lineHeight: '1.5'
          }}>
            هذه الإجابة قيمة واحدة - لا تحتاج إلى رسم بياني
          </div>
        </VegaContainer>
      ) : hasWorkingChart ? (
        <>
          {/* Show split reason for multi-chart */}
          {isMultiChart && message.visualization.split_reason && (
            <DataSection type="reasoning">
              <div className="label">📊 تم إنشاء رسوم بيانية متعددة:</div>
              <div className={`content ${detectLanguage(String(message.visualization.split_reason))}`}>
                {String(message.visualization.split_reason)}
              </div>
            </DataSection>
          )}

          {/* Adjustment feedback */}
          {message.visualization.adjustment_feedback && (
            <AdjustmentFeedback feedback={message.visualization.adjustment_feedback} />
          )}

          {/* Render charts */}
          {isMultiChart ? (
            <MultiChartContainer chartCount={chartsList.length}>
              {chartsList.map((chartItem, index) => (
                <ChartCard key={index}>
                  {chartItem.title && <ChartTitle>{chartItem.title}</ChartTitle>}
                  {chartItem.reasoning && (
                    <DataSection type="reasoning">
                      <div className="label">🤔 سبب الرسم البياني:</div>
                      <div className={`content ${detectLanguage(String(chartItem.reasoning))}`}>
                        {decodeUnicodeText(String(chartItem.reasoning))}
                      </div>
                    </DataSection>
                  )}
                  <SingleChartRenderer
                    chartItem={chartItem}
                    chartIndex={index}
                    onAdjust={handleOpenAdjust}
                    onExpand={handleExpandChart}
                    canAdjust={canAdjustChart}
                    isProcessing={isProcessing}
                    sqlQuery={message.sql_query}
                    originalQuestion={userQuestion}
                    columnNames={message.column_names}
                    cachedResults={message.results}
                  />
                </ChartCard>
              ))}
            </MultiChartContainer>
          ) : (
            <>
              {/* Single chart - show reasoning */}
              {chartsList[0]?.reasoning && (
                <DataSection type="reasoning">
                  <div className="label">🤔 سبب الرسم البياني:</div>
                  <div className={`content ${detectLanguage(String(chartsList[0].reasoning))}`}>
                    {decodeUnicodeText(String(chartsList[0].reasoning))}
                  </div>
                </DataSection>
              )}
              <SingleChartRenderer
                chartItem={chartsList[0]}
                chartIndex={0}
                onAdjust={handleOpenAdjust}
                onExpand={handleExpandChart}
                canAdjust={canAdjustChart}
                isProcessing={isProcessing}
                sqlQuery={message.sql_query}
                originalQuestion={userQuestion}
                columnNames={message.column_names}
                cachedResults={message.results}
              />
            </>
          )}

          <ChartAdjustmentPanel
            isOpen={showAdjustPanel}
            onClose={() => setShowAdjustPanel(false)}
            onAdjust={handleAdjustChart}
            isAdjusting={isProcessing}
            availableColumns={chartSpecificColumns || availableColumns}
            currentChart={currentChart}
            messageData={message}
            chartSpecificData={chartSpecificData}
            chartSpecificColumns={chartSpecificColumns}
            lastAdjustmentError={lastAdjustmentError}
          />
        </>
      ) : (
        <NoVisualization>
          📊 No visualization data available
        </NoVisualization>
      )}

      {/* Fullscreen Modal */}
      <AnimatePresence>
        {showModal && chartsList[modalChartIndex] && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  {chartsList[modalChartIndex].title ||
                   chartsList[modalChartIndex].unit ||
                   'Chart View'}
                </ModalTitle>
                <CloseButton onClick={handleCloseModal}>
                  <X />
                </CloseButton>
              </ModalHeader>
              <ModalBody>
                <div className="chart-container">
                  <ModalChartRenderer chartItem={chartsList[modalChartIndex]} />
                </div>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

// Modal chart renderer component (separate to ensure proper ref lifecycle)
const ModalChartRenderer = ({ chartItem }) => {
  const vegaRef = useRef(null);

  const vegaSpec = React.useMemo(() => {
    if (!chartItem?.chart_schema) {
      return null;
    }

    // Clone the chart schema and override dimensions for fullscreen
    const modalSpec = JSON.parse(JSON.stringify(chartItem.chart_schema));

    // Use larger dimensions for modal
    modalSpec.width = 1200;
    modalSpec.height = 700;

    return modalSpec;
  }, [chartItem?.chart_schema]);

  useVegaChart(vegaRef, vegaSpec);

  return <div ref={vegaRef} />;
};

export default DataVisualization;